import React, { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Row, Col, Space, Card, Button, Modal } from 'antd';
import { CheckCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router';
import { useMount } from 'ahooks';
import config from 'config';
import { useAccount } from 'ShorePay/hooks/account';
import { useCommonInfo } from 'Common/hooks/info';
import Loading from 'Common/Components/Loading';
import { getReferralLink } from 'utils/advertisingLinks';
import ReferralAdDE from 'assets/images/referral_de.png';
import ReferralAdES from 'assets/images/referral_es.png';
import ReferralAdEN from 'assets/images/referral_en.png';
import CustomerDisplayAd from 'assets/images/customer_display.png';
import {
  useOrganizationToken,
  usePortalSession,
} from 'Settings/hooks/business/subscription';
import ShortcutCard from './views/ShortcutCard';

const Header = (
  <Row css="background: #FFF">
    <h1 css="font-size: 20px; padding-left: 48px">
      <FormattedMessage defaultMessage="Welcome to your new backoffice" />
    </h1>
  </Row>
);

const referralImages: { [key: string]: string } = {
  de: ReferralAdDE,
  en: ReferralAdEN,
  es: ReferralAdES,
};

const getReferralImage = (locale: string) =>
  referralImages[locale] || referralImages.de;

const WelcomeScreen: React.FC<React.PropsWithChildren> = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const state = queryParams.get('state');
  const accQuery = useAccount({
    refetchOnWindowFocus: false,
  });
  const commonApiInfoQuery = useCommonInfo();
  const accountData = commonApiInfoQuery.data?.account;
  const orgTokenQuery = useOrganizationToken({ enabled: true });
  const isAccountActive: boolean | undefined =
    accountData?.permissions.includes('subscriptions.can_use_inventorum');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const portalSessionMutation = usePortalSession();
  const showCBSuccessModal = localStorage.getItem('showCBSuccessModal');
  const [isModalVisible, setIsModalVisible] = useState(false);

  useMount(() => {
    if (commonApiInfoQuery.data === undefined) commonApiInfoQuery.refetch();
  });

  useEffect(() => {
    if (state === 'succeeded' && showCBSuccessModal === 'ON') {
      setIsModalVisible(true);
    } else {
      setIsModalVisible(false);
    }
  }, [state, showCBSuccessModal]);

  const redirectActivate = () => {
    // Always redirect to Chargebee subscription management
    if (!isAccountActive) {
      navigate('/settings/general?_=2');
    } else {
      portalSessionMutation.mutate(
        {
          org_token: orgTokenQuery?.data?.org_token,
          merchant_id: accountData?.external_id,
        },
        {
          onSuccess: () => {
            setIsModalOpen(true);
          },
        }
      );
    }
  };
  if (isAccountActive === undefined) {
    return (
      <>
        {Header}
        <LoadingOutlined
          style={{ fontSize: 32 }}
          css="position: fixed; top: 50%;left: 50%;"
          spin
        />
      </>
    );
  }

  const renderModalContent = (): React.ReactNode => {
    if (portalSessionMutation.isPending) {
      return (<Loading noText verticallyCentered />) as React.ReactNode;
    }
    return (
      <iframe
        title="portal-session"
        src={portalSessionMutation.data?.access_url}
        width={360}
        height={530}
        style={{
          border: 'none',
        }}
      />
    ) as React.ReactNode;
  };

  return (
    <>
      {Header}
      <div css="margin: 24px">
        <Row gutter={24}>
          <Col xl={12} md={24} css="margin-bottom: 20px">
            {accQuery.isLoading ? (
              <Loading noText verticallyCentered />
            ) : (
              <Space orientation="vertical" size={24} css="display: flex">
                <Card
                  variant="borderless"
                  title={intl.formatMessage({
                    defaultMessage: 'What is new?',
                  })}
                >
                  <FormattedMessage
                    defaultMessage="Dear Shore user,{breakline} We're delighted to welcome you to the POS Manager and wish you a successful day!{breakline} If you have any questions, we recommend checking out our Help Center, where we've gathered many helpful guides, tips, and tricks for <helpcenterlink>you</helpcenterlink>.{breakline} Of course, you can always reach us through the usual channels if you need help - via email, chat, or phone.{breakline} You can check the current status of your Shore systems <status_link>here</status_link>.{breakline} Quick tip: Make sure to regularly check the bell icon next to your profile in the top right corner. That's where we'll keep you informed about new features.{breakline} Best regards,{breakline} Your Shore team from Berlin, Munich, and around the world"
                    values={{
                      breakline: <br />,
                      helpcenterlink: (chunks: React.ReactNode[]) => (
                        <a
                          href="https://help.shore.com/en"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {chunks}
                        </a>
                      ),
                      status_link: (chunks: React.ReactNode[]) => (
                        <a
                          href="https://shore.instatus.com/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {chunks}
                        </a>
                      ),
                    }}
                  />
                </Card>
                {isAccountActive !== undefined && isAccountActive === false && (
                  <Card
                    variant="borderless"
                    title={intl.formatMessage({
                      defaultMessage:
                        'Your account has expired - Resubscribe now!',
                    })}
                  >
                    <FormattedMessage
                      defaultMessage="Since your subscription has ended you now have only limited access to this system.{breakline} You can now only access the reports and integrations section to download reports, as well as the settings.{breakline} If you want to reactivate your account, click the button below."
                      values={{
                        breakline: <br />,
                      }}
                    />
                    <Row justify="end">
                      <Button
                        type="primary"
                        target="_blank"
                        css={`
                          background: #0099ff;
                          float: right;
                          margin-top: 10px;
                          border-color: #0099ff;
                        `}
                        onClick={redirectActivate}
                      >
                        <FormattedMessage defaultMessage="Resubscribe Now" />
                      </Button>
                    </Row>
                  </Card>
                )}
              </Space>
            )}
          </Col>

          <Col xl={12} md={24}>
            <Row gutter={[24, 24]}>
              {accountData?.source === 'shore' && (
                <Col xl={12} md={24}>
                  <ShortcutCard
                    titleText={intl.formatMessage({
                      defaultMessage: 'Booking and calendar',
                    })}
                    bodyText={intl.formatMessage({
                      defaultMessage: 'Manage your bookings',
                    })}
                    buttonText={intl.formatMessage({
                      defaultMessage: 'Open',
                    })}
                    buttonLink={config.SHORE_APP_URL}
                  />
                </Col>
              )}

              <Col xl={12} md={24}>
                <ShortcutCard
                  titleText={intl.formatMessage({
                    defaultMessage: 'Integrated webshop',
                  })}
                  bodyText={
                    <FormattedMessage
                      defaultMessage="<bold>Important Update:</bold> We no longer offer the online shop for new customers. Alternatively, we offer a website with integrated online shop. <span>Simply send us an email to<mail>service@shore.com</mail> or <link> book an appointment</link> with our expert for more information</span>"
                      values={{
                        span: (txt: React.ReactNode) => <span>{txt}</span>,
                        bold: (txt: React.ReactNode) => <b>{txt}</b>,
                        link: (txt: React.ReactNode) => (
                          <a
                            href=" https://meetings.hubspot.com/jennifer-berg/telefontermin-mit-jennifer"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {txt}
                          </a>
                        ),
                        mail: (txt: React.ReactNode) => (
                          <a
                            target="_blank"
                            rel="noopener noreferrer"
                            href="mailto:service@shore.com"
                            css="margin: 0px 3px;"
                          >
                            {txt}
                          </a>
                        ),
                      }}
                    />
                  }
                />
              </Col>

              <Col xl={24} md={28}>
                <Card
                  variant="borderless"
                  title={intl.formatMessage({
                    defaultMessage: 'Shore refer-a-friend programme',
                  })}
                >
                  <Space orientation="vertical" size="middle" css="width: 100%">
                    <img
                      css="width: 100%"
                      src={getReferralImage(intl.locale.toLowerCase())}
                      alt="referral banner"
                    />
                    <FormattedMessage defaultMessage="Refer your friends and earn up to €500 per referral. Your friends will receive a 10% discount on their first contract year. You can refer as often as you like." />
                    <Row justify="end">
                      <a
                        href={getReferralLink(intl.locale.toLowerCase())}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button size="small" type="primary">
                          <FormattedMessage defaultMessage="Refer now!" />
                        </Button>
                      </a>
                    </Row>
                  </Space>
                </Card>
              </Col>
              <Col xl={24} md={28}>
                <Card
                  variant="borderless"
                  title={intl.formatMessage({
                    defaultMessage: 'The Customer Display',
                  })}
                >
                  <Space orientation="vertical" size="middle" css="width: 100%">
                    <img
                      css="width: 100%"
                      src={CustomerDisplayAd}
                      alt="customer display ad banner"
                    />
                    <FormattedMessage defaultMessage="Mirror your checkout processes, inform your customers about special promotions and invite them to rate you on Google, for example." />
                    <Row justify="end">
                      <Button
                        size="small"
                        css="margin-right: 15px"
                        href="https://help.shore.com/de/wie-verbinde-ich-mein-kundendisplay-mit-der-shore-pos-app"
                        target="_blank"
                      >
                        <FormattedMessage defaultMessage="More Info" />
                      </Button>
                      <Link to="/settings/customer-display">
                        <Button size="small" type="primary">
                          <FormattedMessage defaultMessage="Setup Now" />
                        </Button>
                      </Link>
                    </Row>
                  </Space>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
      <Modal
        width={430}
        open={isModalOpen}
        footer={null}
        closable
        destroyOnHidden
        onCancel={() => setIsModalOpen(false)}
      >
        {renderModalContent()}
      </Modal>

      <Modal
        open={isModalVisible}
        width={480}
        style={{
          margin: 'auto',
          padding: 10,
        }}
        className="checkout-success-modal"
        title={
          <div
            style={{
              paddingBottom: 5,
              margin: 0,
            }}
          >
            <CheckCircleOutlined css="margin-right: 10px; color: #54B25C" />
            {intl.formatMessage({
              defaultMessage: 'Successfull',
            })}
          </div>
        }
        styles={{ body: { margin: 0, padding: 0 } }}
        footer={
          <Button
            type="primary"
            onClick={() => {
              setIsModalVisible(false);
              commonApiInfoQuery.refetch();
              navigate('/');
            }}
          >
            <FormattedMessage defaultMessage="Okay" />
          </Button>
        }
      >
        <p>
          {intl.formatMessage({
            defaultMessage: `Thank you for activating. We are applying your changes in the background, which may take a minute or two. Please wait a moment and then refresh your browser.`,
          })}
        </p>
      </Modal>
    </>
  );
};

export default WelcomeScreen;
